import { useState, useEffect, useRef } from 'react';
import { api } from '../../../api.js';

/**
 * MovePopover — anchored popover for bulk-moving selected documents.
 *
 * Extracted from BulkActionBar's inline expansion. Same backend call
 * (PATCH /documents/case/{id}/bulk-folder); the UI is now a popover
 * anchored to the Move button instead of expanding the bar.
 */
export default function MovePopover({
  caseId, ids, count, folderPaths = [], anchorRef, onClose, onCompleted,
}) {
  const [dest, setDest] = useState('__unfiled__');
  const [newFolder, setNewFolder] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [anchorRef, onClose]);

  async function handleMove() {
    let target = dest === '__new__' ? newFolder.trim() : dest;
    if (dest === '__new__' && !target) {
      setError('Folder path required');
      return;
    }
    if (target === '__unfiled__') target = null;
    if (target && !target.startsWith('/')) target = '/' + target;

    setBusy(true);
    setError(null);
    try {
      await api.patch(`/documents/case/${caseId}/bulk-folder`, {
        document_ids: ids,
        folder_path: target,
      });
      onCompleted?.(`Moved ${count} file${count === 1 ? '' : 's'}`);
    } catch (err) {
      setError(err.message || 'Move failed');
      setBusy(false);
    }
  }

  return (
    <div ref={ref} className="bulk-popover" role="dialog" aria-label="Move documents">
      <div className="bulk-popover-title">Move {count} doc{count === 1 ? '' : 's'} to:</div>
      <select
        className="bulk-popover-select"
        value={dest}
        onChange={(e) => setDest(e.target.value)}
        disabled={busy}
      >
        <option value="__unfiled__">— Unfiled —</option>
        {folderPaths.map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
        <option value="__new__">+ New folder…</option>
      </select>
      {dest === '__new__' && (
        <input
          type="text"
          className="bulk-popover-input"
          placeholder="/path/to/folder"
          value={newFolder}
          onChange={(e) => setNewFolder(e.target.value)}
          disabled={busy}
          autoFocus
        />
      )}
      {error && <div className="bulk-popover-error">{error}</div>}
      <div className="bulk-popover-actions">
        <button type="button" className="btn btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn btn-sm btn-primary" onClick={handleMove} disabled={busy}>
          {busy ? 'Moving…' : 'Move'}
        </button>
      </div>
    </div>
  );
}
